import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {SynopticFile} from '../../../../model/synoptics/synoptic-file';
import {translate, TranslocoService} from '@ngneat/transloco';
import {MessageService} from 'primeng/api';
import {
  SynopticConfiguration,
  SynopticConfigurationComponent
} from '../../../../model/synoptics/synoptic-configuration-types';
import {FileData} from '../../../../model/import-csv/file-data';
import {CsvSynopticsProcessorService} from '../../../../services/synoptics/csv-synoptics-processor.service';
import {SynopticsPropertiesErrors} from '../../../../services/synoptics/synoptics-properties-errors';
import {CsvSynopticData} from '../../../../model/import-csv/csv-synoptic-data';
import {CsvSynopticsValidationService} from '../../../../services/synoptics/csv-synoptics-validation.service';
import {
  SynopticConfigurationComponentCsv,
  SynopticElementEventPreview,
  SynopticElementStatePreview
} from '../../../../model/synoptics/synoptic-configuration-csv-types';
import {CommonMessagesService} from '../../../../services/common-messages.service';
import {SynopticsService} from '../../../../services/synoptics.service';
import {SynopticStatesRequest} from '../../../../model/requests/synoptic-states-request';
import {SynopticStatesResponse} from '../../../../model/responses/synoptic-states-response';
import {SynopticConfigAdapter} from './synoptic-config-adapter';
import {SynopticConfigCsvTemplate} from './synoptic-config-csv-template';

@Component({
  selector: 'app-synoptics-form-files',
  templateUrl: './synoptics-form-files.component.html',
  styleUrls: ['./synoptics-form-files.component.scss']
})
export class SynopticsFormFilesComponent implements OnInit {
  public rows = 10;
  public first = 0;
  public infoCollapsed = false;
  public synopticsPropertiesErrors: SynopticsPropertiesErrors = new SynopticsPropertiesErrors(this.i18n);
  public options = [
    {label: 'common.text', value: 'text', type: 'static'},
    {label: 'misc.synopticModifierTypes.replaceTextValue', value: 'replaceTextValue', type: 'state'},
    {label: 'misc.synopticModifierTypes.styleByThresholds', value: 'styleByThresholds', type: 'state'},
    {label: 'misc.synopticModifierTypes.styleByContinuousValue', value: 'styleByContinuousValue', type: 'state'},
    {label: 'misc.synopticActionTypes.executeCommand', value: 'executeCommand', type: 'event'},
    {label: 'misc.synopticActionTypes.showPopup', value: 'showPopup', type: 'event'},
    {label: 'misc.synopticActionTypes.closePopup', value: 'closePopup', type: 'event'},
    {label: 'misc.synopticActionTypes.showTooltip', value: 'showTooltip', type: 'event'},
    {label: 'misc.synopticActionTypes.linkToSynoptic', value: 'linkToSynoptic', type: 'event'},
  ];
  public selectedOption: any;
  public configSelected: string;

  @Input() synopticConfiguration: string;
  @Input() tenantUuid: string;
  @Input() organizationUuid: string;
  @Input() validatingConfig: boolean;
  @Output() synopticFilesChange: EventEmitter<SynopticFile[]>;
  @Output() synopticCsvFilesChange: EventEmitter<FileData[]>;
  @Output() csvDataChange: EventEmitter<CsvSynopticData[]>;
  @Output() configBuilt: EventEmitter<SynopticConfigurationComponent[]>;
  @Output() validatingConfigChange: EventEmitter<boolean>;

  @ViewChild('fileInput') attachments: any;

  public configuration: SynopticConfiguration;
  public allowedFileExtensions: string[];
  public currentSynopticFiles: SynopticFile[];
  public currentSynopticCsvFiles: FileData[];
  public processedCsvData: CsvSynopticData[];
  public allowedOperations: string;

  constructor(public commonMessagesService: CommonMessagesService,
              private synopticsService: SynopticsService,
              private messageService: MessageService,
              private csvSynopticsService: CsvSynopticsProcessorService,
              private readonly i18n: TranslocoService,
              private readonly validationService: CsvSynopticsValidationService) {
    this.synopticFilesChange = new EventEmitter<SynopticFile[]>();
    this.synopticCsvFilesChange = new EventEmitter<FileData[]>();
    this.csvDataChange = new EventEmitter<CsvSynopticData[]>();
    this.configBuilt = new EventEmitter<SynopticConfigurationComponent[]>();
    this.allowedFileExtensions = ['.svg', '.csv'];
    this.allowedOperations = this.options.map((option: { label: string, value: string }) => option.value).join(', ');
    this.validatingConfigChange = new EventEmitter<boolean>();
  }

  @Input()
  set synopticFiles(files: SynopticFile[]) {
    this.currentSynopticFiles = files ? [...files] : [];
  }

  @Input()
  set synopticCsvFiles(files: FileData[]) {
    this.currentSynopticCsvFiles = files ? [...files] : [];
  }

  @Input()
  set csvData(data: CsvSynopticData[]) {
    this.processedCsvData = data ? [...data] : [];
  }

  ngOnInit(): void {
    this.configuration = this.synopticConfiguration ?
      JSON.parse(this.synopticConfiguration) :
      { file: '', children: [] };

    if (this.currentSynopticCsvFiles.length === 0) {
      this.csvSynopticsService.cleanData();
    }
  }

  public async uploadFile(fileList: FileList): Promise<void> {
    const csvFiles: File[] = [];
    const svgFiles: File[] = [];

    Array.from(fileList).forEach(file => {
      const fileExtension: string = this.checkExtension(file.name);
      switch (fileExtension) {
        case '.csv':
          if (this.isFileAlreadyUploaded(file.name, this.currentSynopticCsvFiles)) {
            this.showAlreadyUploadedFileWarning(file.name);
          } else {
            csvFiles.push(file);
          }
          break;
        case '.svg':
          if (this.isFileAlreadyUploaded(file.name, this.currentSynopticFiles)) {
            this.showAlreadyUploadedFileWarning(file.name);
          } else {
            svgFiles.push(file);
          }
          break;
        default:
          this.showInvalidFormatError();
      }
    });

    const previousSVGFilesNumber: number = this.currentSynopticFiles.length;

    const promises: Promise<any>[] = [];
    svgFiles.forEach((file: File) => {
      promises.push(this.processSVG(file));
    });

    await Promise.all(promises);

    if (previousSVGFilesNumber < this.currentSynopticFiles.length) {
      this.revalidateCsvIfChangeAffects();
    }

    if (csvFiles.length > 0) {
      this.processCsvFiles(csvFiles);
    }

    this.attachments.nativeElement.value = '';
  }

  public processSVG(file: File): Promise<any> {
    return new Promise((resolve) => {
      const fileReader = new FileReader();
      fileReader.onload = () => {
        const svgData: string = (fileReader.result as string).split(',')[1];
        this.currentSynopticFiles.push({name: file.name, data: svgData});
        this.synopticFilesChange.emit(this.currentSynopticFiles);
        resolve(true);
      };
      fileReader.readAsDataURL(file);
    });
  }

  // Los archivos se almacenan como no comprobados
  public async processCsvFiles(csvFiles: File[]): Promise<void> {
    try {
      const {files, synopticData} = await this.csvSynopticsService.processFiles(csvFiles, this.currentSynopticFiles);

      this.currentSynopticCsvFiles = this.currentSynopticCsvFiles.concat(files);
      this.processedCsvData = synopticData;

      this.synopticCsvFilesChange.emit(this.currentSynopticCsvFiles);
      this.csvDataChange.emit(this.processedCsvData);

    } catch (error) {
      console.error('Error processing CSV files....');
    }
  }

  private showInvalidFormatError(): void {
    const summary = translate('error.summary.invalidFormat');
    const detail = translate('error.files.invalidFormat', {format: 'SVG/CSV'});
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
  }

  private isFileAlreadyUploaded(fileName: string, fileList: SynopticFile[] | FileData[]): boolean {
    return fileList.some(file => (file as SynopticFile)?.name === fileName || (file as FileData)?.file?.name === fileName);
  }

  private showAlreadyUploadedFileWarning(fileName: string): void {
    const summary = translate('warning.summary.alreadyUploaded');
    const detail = translate('warning.files.alreadyUploaded', {fileName});
    this.messageService.add({severity: 'warn', summary, detail, sticky: true});
  }

  public removeSynopticFile(file: SynopticFile): void {
    if (this.isFileBeingUsed(file.name)) {
      return;
    }
    this.currentSynopticFiles = this.currentSynopticFiles.filter(f => f !== file);
    this.synopticFilesChange.emit(this.currentSynopticFiles);

    this.validationService.removeFileFromMap(file.name);

    this.reValidateCsv();
  }

  public isFileBeingUsed(fileName: string): boolean {
    return this.configuration.file === fileName || this.configuration.children.map(c => c.file).includes(fileName);
  }

  public deleteAttachment(index: number): void {
    this.first = 0;

    const removedFile: FileData = this.currentSynopticCsvFiles[index];
    this.currentSynopticCsvFiles.splice(index, 1);

    this.processedCsvData = this.csvSynopticsService.removeProcessedData(removedFile.fileIdentifier);

    this.synopticCsvFilesChange.emit(this.currentSynopticCsvFiles);
    this.csvDataChange.emit(this.processedCsvData);
  }

  private checkExtension(fileName: string): string {
    const fileExtension = `.${fileName.split('.').pop()}`;
    const isValidExtension: boolean = this.allowedFileExtensions.some((extension => extension === fileExtension));
    if (isValidExtension) {
      return fileExtension;
    }
    this.showInvalidFormatError();
    return 'unkown';
  }

  // Se carga la configuración a través de los datos que se encuentran en el servicio.
  // Se emite la configuración a través de un evento para activar la carga de datos
  public validateAndApplyConfig(): void {
    this.validatingConfig = true;
    this.validatingConfigChange.emit(this.validatingConfig);
    let config: SynopticConfigurationComponentCsv[] = [...this.csvSynopticsService.buildConfiguration().values()];

    // Los archivos se marcan como comprobados
    this.currentSynopticCsvFiles.forEach((file): void => {
      file.checked = true;
    });

    const synopticStatesRequest: SynopticStatesRequest = new SynopticStatesRequest(config.flatMap(elements => elements.states),
      config.flatMap(elements => elements.events));
    this.synopticsService.validateStatsSynoptic(synopticStatesRequest, this.tenantUuid, this.organizationUuid)
      .subscribe((synopticStatesResponse: SynopticStatesResponse) => {
        const validStates = synopticStatesResponse.states
          .filter(state => !synopticStatesRequest.states
            .flatMap(request => request.sensorIdentifier)
            .includes(state.sensorIdentifier));
        const validEvents = synopticStatesResponse.events
          .filter(event => !synopticStatesRequest.events
            .flatMap(request => request.action.args)
            .includes(event.action.args));
        config = this.eliminateElements(config, validStates, validEvents);

        this.applyConfiguration(config);
        this.csvSynopticsService.cleanData();
        this.currentSynopticCsvFiles = [];
        this.validatingConfig = false;
        this.validatingConfigChange.emit(this.validatingConfig);
        this.synopticCsvFilesChange.emit(this.currentSynopticCsvFiles);
      }, () => {
        this.validatingConfig = false;
        this.validatingConfigChange.emit(this.validatingConfig);
      });
  }

  public isAllCsvChecked(): boolean {
    return this.currentSynopticCsvFiles.every(file => file.checked === true);
  }

  public existAFileWithErrors(): boolean {
    return this.currentSynopticCsvFiles.every(file => file.fileErrors === null);
  }

  public generateDefaultArgs(modifierType: any): any {
    return SynopticConfigCsvTemplate.generateDefaultArgs(modifierType);
  }

  // Revalidar si se añade un archivo svg cuando existen ficheros con errores.
  // (Por ahora revalida TODOS LOS FICHEROS CSV)
  private revalidateCsvIfChangeAffects(): void {
    if (!this.currentSynopticCsvFiles.every((file) => file.fileErrors === undefined || file.fileErrors.lines.length === 0)) {
      this.reValidateCsv();
    }
  }

  // Revalidar el csv entero (Solo ocurre cuando es el caso anterior o cuando se elimina un svg)
  private reValidateCsv(): void {
    if (this.currentSynopticCsvFiles.length > 0) {
      const csvFiles = this.currentSynopticCsvFiles.map((file) => file.file);
      this.csvSynopticsService.cleanData();
      this.currentSynopticCsvFiles = [];
      this.processCsvFiles(csvFiles);
    }
  }

  private applyConfiguration(newConfiguration: SynopticConfigurationComponentCsv[]): void {
    this.configBuilt.emit(
      SynopticConfigAdapter.transformSynopticConfigurationComponentCsvToSynopticConfigurationComponent(newConfiguration)
    );
    const summary = this.i18n.translate('info.importSynopticFiles.operationDoneTitle');
    const detail = this.i18n.translate('info.importSynopticFiles.operationDone');
    this.messageService.add({
      severity: 'success',
      summary,
      detail,
      sticky: true
    });
  }

  private eliminateElements(config: SynopticConfigurationComponentCsv[], validStates: SynopticElementStatePreview[],
                            validEvents: SynopticElementEventPreview[]):
    SynopticConfigurationComponentCsv[] {
    const stateMap: Map<number, SynopticElementStatePreview> = new Map<number, SynopticElementStatePreview>();
    const eventMap: Map<number, SynopticElementEventPreview> = new Map<number, SynopticElementEventPreview>();

    const eventTypesToCheck = ['showTooltip', 'linkToSynoptic', 'executeCommand'];

    validStates.forEach((state: SynopticElementStatePreview): void => {
      stateMap.set(state.csvId, state);
    });
    validEvents.forEach((event: SynopticElementEventPreview): void => {
      eventMap.set(event.csvId, event);
    });

    config.forEach((element: SynopticConfigurationComponentCsv): void => {
      element.states = element.states.map(state => {
        if (stateMap.has(state.csvId)) {
          return stateMap.get(state.csvId);
        }
        return null;
      }).filter(state => state !== null); //Elimina los null

      element.events = element.events.map(event => {
        if (eventTypesToCheck.includes(event.action.type)) {
          if (eventMap.has(event.csvId)) {
            return eventMap.get(event.csvId);
          }
          return null;
        }
        return event;
      }).filter(event => event !== null); //Elimina los null
    });
    return config;
  }

  public readonly JSON: JSON = JSON;
}
